import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { WebsocketContext } from '../../../contexts/websocket-context'
import { ChatSessionMessage } from '../../../shared/interfaces/chat/chat-history.interface'
import {
  ChevronDownIcon,
  SparklesIcon,
  ClockIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline'
import { usePostHog } from 'posthog-js/react'
import { POSTHOG } from '../../../utils/posthog-constants'
import { useSelector } from 'react-redux'
import { selectCurrentProject } from '../../../redux/application-slice'
import { Text, CopyButton, ActionIcon, Tooltip, Spoiler } from '@mantine/core'
import clsx from 'clsx'
import {
  useGetChatSessionModelGenerateRFIMutation,
  useGetUserProfileQuery,
} from '../../../redux/api-slice'
import { toast } from 'react-toastify'

interface AIChatConflictProps {
  chatSessionMessage: ChatSessionMessage
  query: string
}

const AIChatConflict: React.FC<AIChatConflictProps> = ({
  chatSessionMessage,
  query,
}) => {
  const websocketContext = useContext(WebsocketContext)
  const { conflicts, onCheckConflicts, conflictsThinking, conflictsWriting } =
    websocketContext
  const [chatSessionMessageID, setChatSessionMessageID] = useState<
    string | null
  >(null)
  const [conflictExpanded, setConflictExpanded] = useState(false)
  const posthog = usePostHog()
  const currentProject = useSelector(selectCurrentProject)
  const [rfiText, setRfiText] = useState<string | null>(null)
  const [isGeneratingRFI, setIsGeneratingRFI] = useState(false)
  const [generateRFI] = useGetChatSessionModelGenerateRFIMutation()
  const { data: user } = useGetUserProfileQuery()

  useEffect(() => {
    if (!chatSessionMessage?.id) {
      return
    }
    setChatSessionMessageID(chatSessionMessage?.id)
  }, [chatSessionMessage?.id])

  useEffect(() => {
    if (!chatSessionMessageID || chatSessionMessage?.conflict) {
      return
    }
    if (conflicts[chatSessionMessageID]) {
      return
    }
    onCheckConflicts(query, chatSessionMessageID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionMessageID])

  const conflictMessage = useMemo(() => {
    if (chatSessionMessage?.conflict) {
      return chatSessionMessage?.conflict.message
    }
    if (!chatSessionMessageID) {
      return null
    }
    const conflictMessages = conflicts[chatSessionMessageID]
    if (conflictMessages) {
      return conflictMessages.content
    }
    return null
  }, [conflicts, chatSessionMessage?.conflict, chatSessionMessageID])

  useEffect(() => {
    if (conflictMessage && conflictMessage.toLowerCase().includes('yes')) {
      setConflictExpanded(true)
    }
  }, [conflictMessage])

  const conflictAnswer = useMemo(() => {
    if (!conflictMessage) {
      return null
    }
    const paragarphs = conflictMessage.split('\n')
    if (paragarphs.length === 0) {
      return null
    }
    const firstParagraph = paragarphs[0]
    if (firstParagraph.toLocaleLowerCase().includes('yes')) {
      return 'There may be conflicts present'
    } else if (firstParagraph.toLocaleLowerCase().includes('no')) {
      return 'No conflicting information detected'
    }
    return firstParagraph
  }, [conflictMessage])

  const onClickExpandConflictToggle = useCallback(() => {
    setConflictExpanded((s) => !s)
    posthog?.capture(POSTHOG.document_chat_conflicts_opened, {
      project_uuid: currentProject?.uuid,
      chat_session_message_id: chatSessionMessageID,
    })
  }, [posthog, currentProject?.uuid, chatSessionMessageID])

  const onClickGenerateRFI = useCallback(async () => {
    if (!chatSessionMessageID) return

    setIsGeneratingRFI(true)
    try {
      const response = await generateRFI(chatSessionMessageID).unwrap()
      setRfiText(response.rfi_text)
      posthog?.capture(POSTHOG.clicked_generate_rfi, {
        project_uuid: currentProject?.uuid,
        chat_session_message_id: chatSessionMessageID,
      })
    } catch (error) {
      toast.error('Failed to generate RFI')
    } finally {
      setIsGeneratingRFI(false)
    }
  }, [chatSessionMessageID, generateRFI])

  const handleCopyClick = useCallback(
    (copy: () => void) => {
      copy()
      posthog?.capture(POSTHOG.copied_generated_rfi, {
        project_uuid: currentProject?.uuid,
        chat_session_message_id: chatSessionMessageID,
      })
    },
    [currentProject?.uuid, chatSessionMessageID, posthog]
  )

  return chatSessionMessageID ? (
    <div className="shadow-xs mt-0.5 flex w-full justify-end text-xs ">
      <div className="w-full rounded bg-gray-50 p-1 pl-1.5">
        {conflictsThinking[chatSessionMessageID] ||
        conflictsWriting[chatSessionMessageID] ? (
          <div className="animate-pulse text-left">
            Checking for conflicts...
          </div>
        ) : null}
        {!conflictsWriting[chatSessionMessageID] &&
        !conflictsThinking[chatSessionMessageID] ? (
          <div className="flex flex-col gap-2">
            {conflictExpanded ? (
              conflictMessage && (
                <div className="flex flex-col gap-2">
                  <div className="whitespace-pre-line">{conflictMessage}</div>
                  {conflictMessage
                    ?.toLowerCase()
                    .includes('potential conflict: yes') &&
                    user?.feature_flags?.generate_rfi && (
                      <>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            <SparklesIcon className="h-3.5 w-3.5 text-indigo-600" />
                            <Text size="sm" fw={500}>
                              Request for Information (RFI)
                            </Text>
                          </div>
                          {!rfiText && !isGeneratingRFI && (
                            <button
                              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-indigo-600"
                              onClick={onClickGenerateRFI}
                            >
                              Generate
                            </button>
                          )}
                          {isGeneratingRFI && (
                            <div className="flex items-center gap-2 text-gray-500">
                              <ClockIcon className="h-4 w-4 animate-spin" />
                              <Text size="xs">Generating RFI...</Text>
                            </div>
                          )}
                          {rfiText && (
                            <CopyButton value={rfiText} timeout={2000}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? 'Copied' : 'Copy to clipboard'
                                  }
                                >
                                  <ActionIcon
                                    onClick={() => handleCopyClick(copy)}
                                    className={clsx(
                                      'transition-colors',
                                      copied
                                        ? 'text-green-600'
                                        : 'text-white hover:text-white'
                                    )}
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          )}
                        </div>
                        {rfiText && (
                          <div className="rounded-b-lg bg-white p-3">
                            <div className="relative">
                              <Spoiler
                                maxHeight={50}
                                showLabel="Show more"
                                hideLabel="Hide"
                                styles={{
                                  control: {
                                    fontSize: '12px',
                                  },
                                }}
                              >
                                <Text
                                  size="xs"
                                  className="whitespace-pre-wrap text-gray-900"
                                >
                                  {rfiText}
                                </Text>
                              </Spoiler>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                </div>
              )
            ) : (
              <button
                className="h-full w-full text-left"
                onClick={onClickExpandConflictToggle}
              >
                <div className="flex">
                  {conflictAnswer} <ChevronDownIcon className="h-4 w-4" />
                </div>
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  ) : null
}

export default AIChatConflict
